/**
 * @fileoverview Image preview hover tooltip component.
 *
 * Shows a larger preview on hover with label and optional source info
 * (matches VideoPreviewOverlay style).
 */

import React from 'react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

export interface ImagePreviewHoverProps {
  src: string
  label?: React.ReactNode | null // e.g., "Start Frame", "@Image1", "@Element1"
  sourceLabel?: string | null // e.g., shot ID "001" or filename
  children: React.ReactNode
  isDragging?: boolean // Force tooltip closed during drag operations
  /** Side the preview pops on — pass 'right' for triggers docked at the left screen edge. */
  side?: 'left' | 'right' | 'top' | 'bottom'
}

/**
 * Image Preview Hover Component
 * Shows a larger preview on hover with label and optional source info (matches VideoPreviewOverlay style)
 * When isDragging is true, tooltip is force-closed to prevent interference during drag operations
 */
export function ImagePreviewHover({
  src,
  label,
  sourceLabel,
  children,
  isDragging = false,
  side = 'left',
}: ImagePreviewHoverProps) {
  const [open, setOpen] = React.useState(false)
  const showInfoBar = Boolean(label || sourceLabel)

  // Close on drag start — adjusted during render (guarded) instead of an
  // effect, which would trigger a cascading render.
  const [prevDragging, setPrevDragging] = React.useState(isDragging)
  if (prevDragging !== isDragging) {
    setPrevDragging(isDragging)
    if (isDragging) setOpen(false)
  }

  return (
    <TooltipProvider delayDuration={0} disableHoverableContent>
      <Tooltip open={!isDragging && open} onOpenChange={nextOpen => setOpen(isDragging ? false : nextOpen)}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side} className="p-0 overflow-hidden rounded-none border-0 shadow-xl bg-transparent">
          <div className="flex flex-col">
            <img
              src={src}
              alt={typeof label === 'string' ? label : ''}
              className="max-w-80 max-h-60 object-contain bg-black"
            />
            {showInfoBar && (
              <div className="flex justify-between items-center">
                {sourceLabel && (
                  <div className="px-1 py-0.5 bg-black/80 text-white/40 text-[13px] font-mono">{sourceLabel}</div>
                )}
                {label && (
                  <div className="px-1 py-0.5 bg-black/80 text-white/70 text-[13px] font-mono ms-auto">{label}</div>
                )}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
